import { feature as topoFeature } from 'topojson-client';
import type { Topology, GeometryCollection } from 'topojson-specification';
import type { Admin1Dataset, CountryPolygonsTopology } from '../data/schemas';

/**
 * Decode a TopoJSON dataset into a flat list of rings in [lon, lat]
 * coordinates. Iterates every feature in the named object-collection, takes
 * each feature's outer rings (Polygon or MultiPolygon), and returns them
 * all. Holes are ignored — border line rendering only draws perimeters.
 *
 * The `objectKey` parameter selects which collection in `topology.objects`
 * to decode. Country data uses `"countries"`; admin1 uses `"admin1"`.
 */
function decodeRingsFromTopology(
  dataset: { topology: unknown },
  objectKey: string,
): [number, number][][] {
  const topology = dataset.topology as Topology;
  const collection = topology.objects[objectKey] as GeometryCollection | undefined;
  if (!collection) return [];

  const fc = topoFeature(topology, collection);
  if (!('features' in fc)) return [];

  const rings: [number, number][][] = [];
  for (const feat of fc.features) {
    const geom = feat.geometry;
    if (!geom) continue;

    if (geom.type === 'Polygon') {
      if (geom.coordinates[0]) rings.push(geom.coordinates[0] as [number, number][]);
    } else if (geom.type === 'MultiPolygon') {
      for (const poly of geom.coordinates) {
        if (poly[0]) rings.push(poly[0] as [number, number][]);
      }
    }
  }

  return rings;
}

/** Decode country-polygons.json into a flat list of country outer rings. */
export function decodeCountryRings(dataset: CountryPolygonsTopology): [number, number][][] {
  return decodeRingsFromTopology(dataset, 'countries');
}

/** Decode admin1-{ISO}.json into a flat list of state/province outer rings. */
export function decodeAdmin1Rings(dataset: Admin1Dataset): [number, number][][] {
  return decodeRingsFromTopology(dataset, 'admin1');
}

/**
 * One sub-polygon of a country: outer ring + 0 or more holes. The holes
 * carve out enclaves like Lesotho-inside-South-Africa or Vatican-inside-Italy.
 * All rings in [lon, lat] order, matching the ring representation used
 * elsewhere in the engine.
 */
export interface PolygonWithHoles {
  outer: [number, number][];
  holes: [number, number][][];
}

/** All sub-polygons that make up one country, keyed by ISO 3166-1 alpha-2. */
export interface CountryPolygonGroup {
  iso: string;
  polygons: PolygonWithHoles[];
}

/**
 * Decode country-polygons.json into per-country polygon groups WITH holes
 * preserved. Used by the fills layer (§7b), which triangulates each polygon
 * with earcut and needs hole rings to carve enclaves out of the outer fill.
 *
 * Borders use {@link decodeCountryRings} which discards holes — borders are
 * just line segments and don't care about inside vs outside.
 */
export function decodeCountryPolygons(dataset: CountryPolygonsTopology): CountryPolygonGroup[] {
  const topology = dataset.topology as Topology;
  const collection = topology.objects.countries as GeometryCollection | undefined;
  if (!collection) return [];

  const fc = topoFeature(topology, collection);
  if (!('features' in fc)) return [];

  const groups: CountryPolygonGroup[] = [];
  for (const feat of fc.features) {
    const geom = feat.geometry;
    if (!geom) continue;
    // Feature ID is set to the country's ISO A2 in scripts/build-data.mjs.
    // Skip features without one — anything coming through the pipeline has
    // it, but defensive against future data sources.
    const iso = typeof feat.id === 'string' ? feat.id : null;
    if (!iso) continue;

    const polygons: PolygonWithHoles[] = [];
    if (geom.type === 'Polygon') {
      const [outer, ...holes] = geom.coordinates as [number, number][][];
      if (outer) polygons.push({ outer, holes });
    } else if (geom.type === 'MultiPolygon') {
      for (const poly of geom.coordinates as [number, number][][][]) {
        const [outer, ...holes] = poly;
        if (outer) polygons.push({ outer, holes });
      }
    }
    if (polygons.length > 0) groups.push({ iso, polygons });
  }

  return groups;
}
